import { useBlockEditorContext } from "..";
import { NodeViewWrapper } from "@tiptap/react";
import { useQuery } from "react-query";
import { t } from "@/i18n-js/instance";
import { CloseButton } from "@/react/components/shared/uikit/TipTapBlockEditor/FileExtension/CloseButton";
import { useAttachmentGallery } from "@circle-react/components/Modals/AttachmentGalleryModal";
import { Typography } from "@circle-react/components/shared/uikit/Typography";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { Icon } from "@circle-react-shared/Icon";
import { supportsGalleryPreview } from "@circle-react-shared/uikit/TipTap/utilities/contentTypeParser";
import { OriginalSizeImagePreview } from "./EmailImage/OriginalSizeImagePreview";
import { ImageContainer } from "./ImageContainer";

export interface ImagePreviewProps {
  deleteNode: () => void;
  editor: any;
  node: any;
  updateAttributes: (attrs: any) => void;
}

export const ImagePreview = ({
  editor,
  node,
  updateAttributes,
  deleteNode,
}: ImagePreviewProps) => {
  const { show: openAttachmentGallery } = useAttachmentGallery();
  const isEditable = editor.isEditable;
  const {
    localInlineAttachments,
    inlineAttachments,
    addToLocalInlineAttachments,
    isWithinEmailEditor,
  } = useBlockEditorContext() ?? {};

  const signedId = node.attrs?.signed_id;
  const attachmentMeta =
    localInlineAttachments?.find(
      attachment => attachment.signed_id === signedId,
    ) ||
    inlineAttachments?.find(attachment => attachment.signed_id === signedId);

  const attachmentsForGalleryPreview = inlineAttachments?.filter(a =>
    supportsGalleryPreview(a.content_type),
  );

  const { isLoading } = useQuery(
    internalApi.richTextFields.fetchAttachment({ signedId }),
    {
      enabled: !attachmentMeta && isEditable,
      retry: false,
      retryOnMount: false,
      onSuccess: addToLocalInlineAttachments,
    },
  );

  const onClickOpenAttachmentGallery = () => {
    if (isEditable) return;
    void openAttachmentGallery({
      attachments: attachmentsForGalleryPreview,
      startIndex: attachmentsForGalleryPreview?.findIndex(
        a => a.signed_id == attachmentMeta.signed_id,
      ),
    });
  };

  if (isLoading) {
    return (
      <NodeViewWrapper as="div" className="my-4">
        <Icon type="loader" size={16} />
      </NodeViewWrapper>
    );
  }

  if (!attachmentMeta) {
    return (
      <NodeViewWrapper
        as="div"
        className="bg-tertiary group flex items-start gap-2 rounded-md p-4"
      >
        {isEditable && <CloseButton onClick={deleteNode} />}
        <Icon type="16-trash-bin-2" size={20} />

        <Typography.BodySm as="span">
          {t("tiptap.file_not_found")}
        </Typography.BodySm>
      </NodeViewWrapper>
    );
  }

  if (
    isWithinEmailEditor &&
    (node.attrs.use_intrinsic_size || node.attrs.href)
  ) {
    return (
      <NodeViewWrapper as="div">
        <OriginalSizeImagePreview
          node={node}
          editor={editor}
          attachmentMeta={attachmentMeta}
          updateAttributes={updateAttributes}
          deleteNode={deleteNode}
        />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="div">
      <ImageContainer
        node={node}
        editor={editor}
        attachmentMeta={attachmentMeta}
        onClickOpenAttachmentGallery={onClickOpenAttachmentGallery}
        updateAttributes={updateAttributes}
        deleteNode={deleteNode}
      />
    </NodeViewWrapper>
  );
};
